import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';

// Helper function to get room name
const getRoomName = (roomId, roomsData) => {
    if (!roomsData || !Array.isArray(roomsData)) return 'Unknown';
    const room = roomsData.find(r => parseInt(r.id) === parseInt(roomId));
    return room ? room.roomtype : 'Unknown';
};

// Helper function to get status name
const getStatusName = (status) => {
    switch(status) {
        case 0: return 'Cancelled';
        case 1: return 'Active';
        case 2: return 'Checked In';
        case 3: return 'Checked Out';
        default: return 'Unknown';
    }
};

// Helper function to get booking type name
const getBookingTypeName = (bookingType) => {
    switch(parseInt(bookingType)) {
        case 1: return 'Individual';
        case 2: return 'Organisation';
        default: return 'Unknown';
    }
};

// Helper function to get customer/organisation display name
const getCustomerDisplayName = (record) => {
    if (!record) return 'N/A';
    if (record.booking_type === 2) {
        return record.organisation_name || 'N/A';
    }
    return record.customer_name || 'N/A';
};

// Helper function to get customer mobile
const getCustomerMobile = (record) => {
    if (!record) return 'N/A';
    if (record.booking_type === 2) {
        if (record.organisation_members && record.organisation_members.length > 0) {
            return record.organisation_members[0].phone || 'N/A';
        }
        return 'N/A';
    }
    return record.customer_mobile || 'N/A';
};

// Helper function to get total guests count
const getTotalGuests = (record) => {
    if (!record) return 0;
    if (record.booking_type === 2) {
        return record.organisation_members?.length || 0;
    }
    return (record.adults || 1) + (record.children || 0);
};

// Helper function to format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount) + " sh";
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
};

// Helper function to format datetime
const formatDateTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-GB');
};

// Helper function to draw table manually
const drawCheckInTable = (doc, data, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const margin = 20;
    const rowHeight = 12;

    // Column widths for 7 columns (removed Branch)
    const colWidths = [35, 45, 35, 35, 40, 35, 30]; // Total: 255

    // Check if we need a new page
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;

    if (currentY + neededHeight > pageHeight - 20) {
        doc.addPage('landscape');
        currentY = margin;
        
        // Redraw header on new page
        doc.setFillColor(255, 255, 255);
        doc.setTextColor(25, 118, 210);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['Booking #', 'Customer/Org', 'Check In', 'Check Out', 'Room Type', 'Amount', 'Status'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(25, 118, 210);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }

    // Draw table header if not on new page
    if (startY === currentY) {
        doc.setFillColor(25, 118, 210);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        
        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        const headers = ['Booking #', 'Customer/Org', 'Check In', 'Check Out', 'Room Type', 'Amount', 'Status'];
        let xPos = margin;
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        // Draw header underline
        doc.setDrawColor(255, 255, 255);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
    }

    // Draw table rows
    data.forEach((row, rowIndex) => {
        // Check for page break
        if (currentY + rowHeight > pageHeight - 20) {
            doc.addPage('landscape');
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            // Draw header background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            const headers = ['Booking #', 'Customer/Org', 'Check In', 'Check Out', 'Room Type', 'Amount', 'Status'];
            let xPos = margin;
            
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 8);
                xPos += colWidths[index];
            });
            
            // Draw header underline
            doc.setDrawColor(255, 255, 255);
            doc.setLineWidth(0.5);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(245, 245, 245);
        } else {
            doc.setFillColor(255, 255, 255);
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        // Draw row borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.2);
        doc.line(margin, currentY, pageWidth - margin, currentY);
        
        // Draw row text
        let xPos = margin;
        row.forEach((cell, cellIndex) => {
            // Truncate text if too long
            let text = String(cell);
            if (cellIndex === 1 && text.length > 20) { // Customer/Org name
                text = text.substring(0, 17) + '...';
            } else if (text.length > 25) {
                text = text.substring(0, 22) + '...';
            }
            
            doc.text(text, xPos + 2, currentY + 8);
            xPos += colWidths[cellIndex];
        });
        
        currentY += rowHeight;
    });

    return currentY;
};

// Helper function to format organisation members for Excel
const formatOrganisationMembers = (members, roomsData) => {
    if (!members || members.length === 0) return 'N/A';
    return members.map(m => `${m.name} (${m.phone}) - Room: ${getRoomName(m.room_type, roomsData)}`).join('; ');
};

// PDF export for Check-ins - Shows only specified columns in landscape
export const exportToPDF = (checkIns, roomsData = [], filters = {}) => {
    try {
        // Create PDF in landscape mode
        const doc = new jsPDF('landscape');
        
        // Set document properties
        doc.setProperties({
            title: 'Check-ins Report',
            subject: 'Check-ins Export',
            author: companyInfo.name,
            keywords: 'check-ins, export, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('CHECK-INS REPORT', doc.internal.pageSize.width / 2, 20, { align: 'center' });
        
        // Company/System name
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text(companyInfo.name, doc.internal.pageSize.width / 2, 30, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 40;
        
        // Report info in two columns
        const leftColumnX = 20;
        const rightColumnX = doc.internal.pageSize.width / 2 + 20;
        
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
        doc.text(`Total Check-ins: ${checkIns.length}`, rightColumnX, currentY);
        currentY += 8;
        
        // Calculate totals
        if (checkIns.length > 0) {
            const totalAmount = checkIns.reduce((sum, checkIn) => sum + parseFloat(checkIn.total_amount || 0), 0);
            const individualBookings = checkIns.filter(checkIn => checkIn.booking_type === 1).length;
            const organisationBookings = checkIns.filter(checkIn => checkIn.booking_type === 2).length;
            
            doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, leftColumnX, currentY);
            doc.text(`Individual: ${individualBookings}`, rightColumnX, currentY);
            currentY += 8;
            doc.text(`Organisation: ${organisationBookings}`, leftColumnX, currentY);
            currentY += 8;
        }
        
        // Filters applied
        if (filters.booking_no || filters.customer_name || filters.dateFrom || filters.dateTo || filters.status) {
            doc.setFontSize(9);
            doc.setTextColor(60, 60, 60);
            doc.text('Applied Filters:', leftColumnX, currentY);
            currentY += 6;
            
            let filterLines = [];
            if (filters.booking_no) filterLines.push(`• Booking No: ${filters.booking_no}`);
            if (filters.customer_name) filterLines.push(`• Customer/Org: ${filters.customer_name}`);
            if (filters.status) filterLines.push(`• Status: ${getStatusName(filters.status)}`);
            if (filters.dateFrom) filterLines.push(`• From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterLines.push(`• To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            
            // Display filters in two columns if many
            const midPoint = Math.ceil(filterLines.length / 2);
            const leftFilters = filterLines.slice(0, midPoint);
            const rightFilters = filterLines.slice(midPoint);
            
            const maxLines = Math.max(leftFilters.length, rightFilters.length);
            
            for (let i = 0; i < maxLines; i++) {
                if (leftFilters[i]) {
                    doc.text(leftFilters[i], leftColumnX + 5, currentY);
                }
                if (rightFilters[i]) {
                    doc.text(rightFilters[i], rightColumnX, currentY);
                }
                currentY += 5;
            }
            currentY += 5;
        }
        
        // Prepare table data with only specified columns
        const tableData = checkIns.map((checkIn) => [
            checkIn.booking_no || '',
            getCustomerDisplayName(checkIn),
            checkIn.check_in ? formatDate(checkIn.check_in) : '',
            checkIn.check_out ? formatDate(checkIn.check_out) : '',
            getRoomName(checkIn.room_type, roomsData),
            formatCurrency(checkIn.total_amount || 0),
            getStatusName(checkIn.status)
        ]);
        
        // Draw the table
        currentY = drawCheckInTable(doc, tableData, currentY + 10);
        
        // Add footer with page number
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(
                `Page ${i} of ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
            doc.text(
                `Generated on: ${new Date().toLocaleDateString()}`,
                doc.internal.pageSize.width - 20,
                doc.internal.pageSize.height - 10,
                { align: 'right' }
            );
        }
        
        const fileName = `checkins_report_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Check-ins PDF export failed:', error);
        throw new Error('Failed to export to PDF: ' + error.message);
    }
};

// Excel export for Check-ins - Shows all attributes with organisation support
export const exportToExcel = (checkIns, { roomsData = [] } = {}, filters = {}) => {
    try {
        // Prepare data for Excel with all attributes
        const excelData = checkIns.map((checkIn, index) => {
            // Base data
            const baseData = {
                'No.': index + 1,
                'Booking ID': checkIn.id,
                'Booking No': checkIn.booking_no,
                'Booking Type': getBookingTypeName(checkIn.booking_type),
                'Check In Date': checkIn.check_in ? new Date(checkIn.check_in).toLocaleDateString('en-GB') : '',
                'Check In Time': checkIn.check_in ? new Date(checkIn.check_in).toLocaleTimeString('en-GB') : '',
                'Actual Check In': checkIn.actual_check_in ? new Date(checkIn.actual_check_in).toLocaleString('en-GB') : '',
                'Check Out Date': checkIn.check_out ? new Date(checkIn.check_out).toLocaleDateString('en-GB') : '',
                'Check Out Time': checkIn.check_out ? new Date(checkIn.check_out).toLocaleTimeString('en-GB') : '',
                'Room ID': checkIn.room_type,
                'Room': getRoomName(checkIn.room_type, roomsData),
                'Adults': checkIn.adults || 1,
                'Children': checkIn.children || 0,
                'Total Guests': getTotalGuests(checkIn),
                'Booking Charge': parseFloat(checkIn.booking_charge || 0),
                'Advance Amount': parseFloat(checkIn.advance_amount || 0),
                'Total Amount': parseFloat(checkIn.total_amount || 0),
                'Balance Due': parseFloat(checkIn.total_amount || 0) - parseFloat(checkIn.advance_amount || 0),
                'Payment Mode': checkIn.payment_mode || '',
                'Status': getStatusName(checkIn.status),
                'Checked In By': checkIn.checked_in_by || '',
                'Check In Comments': checkIn.check_in_comments || '',
                'Booking Remarks': checkIn.booking_remarks || '',
                'Created At': new Date(checkIn.created_at).toLocaleString('en-GB'),
                'Updated At': new Date(checkIn.updated_at).toLocaleString('en-GB')
            };

            // Add type-specific fields
            if (checkIn.booking_type === 1) { // Individual
                return {
                    ...baseData,
                    'Customer Name': checkIn.customer_name || '',
                    'Customer Mobile': checkIn.customer_mobile || '',
                    'Customer Email': checkIn.customer_email || '',
                    'Organisation Name': 'N/A',
                    'Organisation Members': 'N/A'
                };
            } else { // Organisation
                return {
                    ...baseData,
                    'Customer Name': 'N/A',
                    'Customer Mobile': 'N/A',
                    'Customer Email': 'N/A',
                    'Organisation Name': checkIn.organisation_name || '',
                    'Organisation Members': formatOrganisationMembers(checkIn.organisation_members, roomsData)
                };
            }
        });

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Check-ins');

        // Generate Excel file and download
        const fileName = `checkins_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Check-ins Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// CSV export for Check-ins - Shows all attributes with organisation support
export const exportToCSV = (checkIns, { roomsData = [] } = {}) => {
    try {
        // Determine headers based on booking types present
        const hasOrganisation = checkIns.some(ci => ci.booking_type === 2);
        
        const headers = [
            'Booking ID',
            'Booking No',
            'Booking Type',
            'Customer Name',
            'Customer Mobile',
            'Customer Email',
            'Organisation Name',
            'Organisation Members',
            'Check In Date',
            'Check In Time',
            'Actual Check In',
            'Check Out Date',
            'Check Out Time',
            'Room ID',
            'Room Name',
            'Adults',
            'Children',
            'Total Guests',
            'Booking Charge',
            'Advance Amount',
            'Total Amount',
            'Balance Due',
            'Payment Mode',
            'Status',
            'Checked In By',
            'Check In Comments',
            'Booking Remarks',
            'Created At',
            'Updated At'
        ];
        
        const csvData = checkIns.map(checkIn => {
            const row = [
                checkIn.id,
                checkIn.booking_no,
                `"${getBookingTypeName(checkIn.booking_type)}"`,
                checkIn.booking_type === 1 ? `"${(checkIn.customer_name || '').replace(/"/g, '""')}"` : '""',
                checkIn.booking_type === 1 ? `"${(checkIn.customer_mobile || '').replace(/"/g, '""')}"` : '""',
                checkIn.booking_type === 1 ? `"${(checkIn.customer_email || '').replace(/"/g, '""')}"` : '""',
                checkIn.booking_type === 2 ? `"${(checkIn.organisation_name || '').replace(/"/g, '""')}"` : '""',
                checkIn.booking_type === 2 ? `"${formatOrganisationMembers(checkIn.organisation_members, roomsData).replace(/"/g, '""')}"` : '""',
                checkIn.check_in ? new Date(checkIn.check_in).toLocaleDateString('en-GB') : '',
                checkIn.check_in ? new Date(checkIn.check_in).toLocaleTimeString('en-GB') : '',
                checkIn.actual_check_in ? formatDateTime(checkIn.actual_check_in) : '',
                checkIn.check_out ? new Date(checkIn.check_out).toLocaleDateString('en-GB') : '',
                checkIn.check_out ? new Date(checkIn.check_out).toLocaleTimeString('en-GB') : '',
                checkIn.room_type,
                `"${getRoomName(checkIn.room_type, roomsData).replace(/"/g, '""')}"`,
                checkIn.adults || 1,
                checkIn.children || 0,
                getTotalGuests(checkIn),
                parseFloat(checkIn.booking_charge || 0).toFixed(2),
                parseFloat(checkIn.advance_amount || 0).toFixed(2),
                parseFloat(checkIn.total_amount || 0).toFixed(2),
                (parseFloat(checkIn.total_amount || 0) - parseFloat(checkIn.advance_amount || 0)).toFixed(2),
                `"${(checkIn.payment_mode || '').replace(/"/g, '""')}"`,
                getStatusName(checkIn.status),
                `"${(checkIn.checked_in_by || '').replace(/"/g, '""')}"`,
                `"${(checkIn.check_in_comments || '').replace(/"/g, '""')}"`,
                `"${(checkIn.booking_remarks || '').replace(/"/g, '""')}"`,
                `"${formatDateTime(checkIn.created_at)}"`,
                `"${formatDateTime(checkIn.updated_at)}"`
            ];
            return row;
        });

        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `checkins_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Check-ins CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};